/**
 * RoleAccessComparisonPage — Admin tool for comparing field-level access across roles.
 *
 * Shows side-by-side lens evaluation results: which fields each role can and cannot see.
 * Uses compareLensEvaluation backend query (DRR-103).
 *
 * DRR-201: Roles now fetched from roleRegistry query (single source of truth).
 *
 * Accessible from Admin > Role Access Comparison.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  MultiSelect,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconScale,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

import {
  useCompareLensEvaluation,
  RoleComparisonPanel,
} from '@ferp/workflow-forms';
import type { RoleSetInput } from '@ferp/workflow-forms';

// ============================================================================
// DRR-201: Role Registry Query — single source of truth
// ============================================================================

const GET_ROLE_REGISTRY = gql`
  query GetRoleRegistry {
    roleRegistry {
      roles {
        id
        name
        type
        source
        description
        wall
        subgraphs
        inherits
      }
    }
  }
`;

interface RoleRegistryEntry {
  id: string;
  name: string;
  type: string;
  source: string | null;
  description: string | null;
  wall: string | null;
  subgraphs: string[];
  inherits: string[];
}

interface RoleRegistryData {
  roleRegistry: {
    roles: RoleRegistryEntry[];
  };
}

// ============================================================================
// Dynamic Presets — generated from role types
// ============================================================================

function generatePresets(roles: RoleRegistryEntry[]): Array<{ label: string; roles: string[] }> {
  const statutory = roles.filter((r) => r.type === 'statutory').map((r) => r.id);
  const operational = roles.filter((r) => r.type === 'operational').map((r) => r.id);
  const clinical = roles.filter((r) => r.wall === 'clinical').map((r) => r.id);
  const base = roles.filter((r) => r.type === 'base').map((r) => r.id);

  return [
    { label: 'Clinical Wall Roles', roles: clinical.slice(0, 5) },
    { label: 'Statutory Roles', roles: statutory.slice(0, 5) },
    { label: 'Operational Roles', roles: operational.slice(0, 5) },
    { label: 'Base Platform Roles', roles: base.slice(0, 3) },
  ].filter((p) => p.roles.length >= 2);
}

// ============================================================================
// Component
// ============================================================================

export function RoleAccessComparisonPage() {
  // DRR-201: Fetch role registry from backend
  const { data: registryData, loading: registryLoading, error: registryError } = useQuery<RoleRegistryData>(
    GET_ROLE_REGISTRY
  );

  const {
    comparison,
    isComparing,
    error,
    compareLenses,
    clearComparison,
  } = useCompareLensEvaluation();

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Transform registry to MultiSelect format
  const roleOptions = useMemo(() => {
    if (!registryData?.roleRegistry?.roles) return [];
    return registryData.roleRegistry.roles
      .filter((r) => typeFilter === 'all' || r.type === typeFilter)
      .map((role) => ({
        value: role.id,
        label: `${role.name}${role.wall ? ` [${role.wall}]` : ''}`,
      }));
  }, [registryData, typeFilter]);

  // Generate presets from registry
  const presets = useMemo(() => {
    if (!registryData?.roleRegistry?.roles) return [];
    return generatePresets(registryData.roleRegistry.roles);
  }, [registryData]);

  // Role lookup for labels
  const roleMap = useMemo(() => {
    if (!registryData?.roleRegistry?.roles) return new Map<string, RoleRegistryEntry>();
    return new Map(registryData.roleRegistry.roles.map((r) => [r.id, r]));
  }, [registryData]);

  const handleRunComparison = useCallback(() => {
    if (selectedRoles.length < 2) return;
    const roleSets: RoleSetInput[] = selectedRoles.map((roleId) => {
      const role = roleMap.get(roleId);
      return {
        roleIds: [roleId],
        label: role?.name ?? roleId,
      };
    });
    compareLenses({ roleSets });
  }, [selectedRoles, roleMap, compareLenses]);

  const handlePreset = useCallback((preset: { label: string; roles: string[] }) => {
    setSelectedRoles(preset.roles);
    const roleSets: RoleSetInput[] = preset.roles.map((roleId) => {
      const role = roleMap.get(roleId);
      return {
        roleIds: [roleId],
        label: role?.name ?? roleId,
      };
    });
    compareLenses({ roleSets });
  }, [roleMap, compareLenses]);

  // Summary stats
  const stats = useMemo(() => {
    if (!comparison) return null;
    return {
      totalFields: comparison.universallyHidden.length + comparison.universallyVisible.length + comparison.mixedAccessFields.length,
      mixedCount: comparison.mixedAccessFields.length,
      hiddenCount: comparison.universallyHidden.length,
      visibleCount: comparison.universallyVisible.length,
    };
  }, [comparison]);

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon variant="light" size="lg" color="grape">
              <IconScale size={20} />
            </ThemeIcon>
            <div>
              <Title order={2}>Role Access Comparison</Title>
              <Text c="dimmed" size="sm">
                Compare field-level visibility across roles through the 4-phase lens chain
              </Text>
            </div>
          </Group>
          {stats && (
            <Group gap="xs">
              <Badge variant="light" color="green" size="lg">
                {stats.visibleCount} shared visible
              </Badge>
              <Badge variant="light" color="yellow" size="lg">
                {stats.mixedCount} mixed access
              </Badge>
              <Badge variant="light" color="red" size="lg">
                {stats.hiddenCount} shared hidden
              </Badge>
            </Group>
          )}
        </Group>

        {/* Registry loading/error */}
        {registryLoading && (
          <Paper p="md" withBorder ta="center">
            <Group gap="sm" justify="center">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">Loading role registry...</Text>
            </Group>
          </Paper>
        )}

        {registryError && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
            Failed to load role registry: {registryError.message}. Using fallback comparison.
          </Alert>
        )}

        {/* Controls */}
        <Paper p="md" withBorder>
          <Stack gap="sm">
            {/* Type filter */}
            <Group gap="sm">
              <Text size="sm" fw={500}>Filter by type:</Text>
              <SegmentedControl
                size="xs"
                value={typeFilter}
                onChange={(val) => {
                  setTypeFilter(val);
                  setSelectedRoles([]);
                  clearComparison();
                }}
                data={[
                  { value: 'all', label: 'All' },
                  { value: 'statutory', label: 'Statutory' },
                  { value: 'operational', label: 'Operational' },
                  { value: 'base', label: 'Base' },
                  { value: 'external', label: 'External' },
                  { value: 'client', label: 'Client' },
                ]}
              />
            </Group>

            <Group gap="sm" align="flex-end">
              <MultiSelect
                label="Roles to compare"
                description="Select 2 or more roles to see field-level access differences"
                data={roleOptions}
                value={selectedRoles}
                onChange={(val) => {
                  setSelectedRoles(val);
                  clearComparison();
                }}
                searchable
                disabled={registryLoading}
                style={{ flex: 1 }}
              />
              <Button
                onClick={handleRunComparison}
                loading={isComparing}
                disabled={selectedRoles.length < 2 || registryLoading}
                leftSection={<IconScale size={16} />}
                color="grape"
                size="md"
              >
                Compare
              </Button>
            </Group>

            {/* Quick presets */}
            {presets.length > 0 && (
              <Group gap="xs">
                <Text size="xs" c="dimmed" fw={500}>Quick presets:</Text>
                {presets.map((preset) => (
                  <Badge
                    key={preset.label}
                    size="sm"
                    variant="light"
                    color="grape"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handlePreset(preset)}
                  >
                    {preset.label}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        </Paper>

        {/* Error */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Comparison failed: {error.message}
          </Alert>
        )}

        {/* Loading */}
        {isComparing && (
          <Paper p="xl" withBorder ta="center">
            <Stack gap="md" align="center">
              <Loader size="md" />
              <Text c="dimmed">Evaluating lens rules for {selectedRoles.length} roles...</Text>
            </Stack>
          </Paper>
        )}

        {/* Results */}
        {comparison && <RoleComparisonPanel comparison={comparison} />}

        {/* Empty state */}
        {!comparison && !isComparing && (
          <Paper p="xl" withBorder ta="center" mih={300}>
            <Stack gap="md" align="center" justify="center" mih={250}>
              <IconShieldCheck size={48} color="gray" />
              <Text size="lg" fw={500}>Select roles and click Compare</Text>
              <Text c="dimmed" size="sm" maw={500}>
                This tool evaluates the 4-phase lens chain (Role, Scope, Setting, Situation)
                for each selected role and shows field-by-field access differences.
                Useful for verifying HIPAA minimum necessary compliance and validating
                per-tenant lens overrides like ALD-CNA oversight.
              </Text>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}
